// libraries.
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col } from 'react-bootstrap';

// components.
import { SimpleInputField } from '@shared/SimpleInputField';

// selectors.
import {
  getSelectedCoordinates,
  updateCoordinates,
} from '../store/slices/georeferencing-slice';

export const CoordinateInputs = () => {
  const dispatch = useDispatch();
  const selectedCoordinates = useSelector(getSelectedCoordinates);

  const [latitude, setLatitude] = useState<string>('');
  const [longitude, setLongitude] = useState<string>('');
  const [errors, setErrors] = useState<{ lat?: string; lng?: string }>({});

  // Update local state when coordinates change from Redux
  useEffect(() => {
    if (selectedCoordinates) {
      setLatitude(selectedCoordinates.lat.toString());
      setLongitude(selectedCoordinates.lng.toString());
    }
  }, [selectedCoordinates]);

  const validateCoordinate = (value: string, type: 'lat' | 'lng'): boolean => {
    // If empty, clear errors for this field
    if (!value || value === '') {
      setErrors(prev => ({ ...prev, [type]: undefined }));
      return false;
    }

    const num = parseFloat(value);

    // parseFloat can parse partial numbers, so we need to check if it's a complete valid number
    if (isNaN(num) || value.trim() === '') {
      setErrors(prev => ({ ...prev, [type]: undefined }));
      return false;
    }

    if (type === 'lat' && (num < -90 || num > 90)) {
      setErrors(prev => ({
        ...prev,
        lat: 'La latitud debe estar entre -90 y 90',
      }));
      return false;
    }

    if (type === 'lng' && (num < -180 || num > 180)) {
      setErrors(prev => ({
        ...prev,
        lng: 'La longitud debe estar entre -180 y 180',
      }));
      return false;
    }

    setErrors(prev => ({ ...prev, [type]: undefined }));
    return true;
  };

  const handleLatitudeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLatitude(value);

    if (value && validateCoordinate(value, 'lat')) {
      const lat = parseFloat(value);
      const lng = longitude ? parseFloat(longitude) : 0;
      dispatch(updateCoordinates({ lat, lng }));
    }
  };

  const handleLongitudeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLongitude(value);

    if (value && validateCoordinate(value, 'lng')) {
      const lat = latitude ? parseFloat(latitude) : 0;
      const lng = parseFloat(value);
      dispatch(updateCoordinates({ lat, lng }));
    }
  };

  return (
    <div>
      <Row>
        <Col xs={12} className="mb-2">
          <SimpleInputField
            label="Latitud"
            name="latitude"
            type="number"
            step="any"
            value={latitude}
            onChange={handleLatitudeChange}
            placeholder={selectedCoordinates?.lat.toString() || 'Ej: 4.60971'}
            error={errors.lat}
          />
        </Col>
        <Col xs={12}>
          <SimpleInputField
            label="Longitud"
            name="longitude"
            type="number"
            step="any"
            value={longitude}
            onChange={handleLongitudeChange}
            placeholder={selectedCoordinates?.lng.toString() || 'Ej: -74.08175'}
            error={errors.lng}
          />
        </Col>
      </Row>
    </div>
  );
};
